import React, { useState, useEffect } from 'react';
import { businessRequiredHint, businessRequiredMessage, isBusinessIdRequired, isTargetMismatch, TARGET_MISMATCH_MESSAGE } from '../utils/apiErrors';
import { BUSINESS_USER_ROLES, DEFAULT_BUSINESS_ROLE, groupNeedsBusiness, hasBusinessId } from '../utils/userAdmin';
import { Drawer, SIZE, ANCHOR } from 'baseui/drawer';
import { Button, KIND, SIZE as BUTTON_SIZE } from 'baseui/button';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Select } from 'baseui/select';
import { Checkbox, LABEL_PLACEMENT } from 'baseui/checkbox';
import { styled } from 'baseui';
import { HeadingSmall, LabelSmall, ParagraphSmall } from 'baseui/typography';
import { Key, Mail, ShieldOff, ShieldCheck, Trash2, User, Save, Link } from 'lucide-react';
import { toaster } from 'baseui/toast';
import type { SAMUser } from '../types/role.types';
import { useResetPassword, useChangePassword, useDisableUser, useEnableUser, useDeleteUser } from '../hooks/useSAMUsers';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { S3Image } from './S3Image';
import { directApiInstance, defaultInstance } from '../api/httpClient';
import { fetchDbUser } from '../api/dbUserLookup';
import { useEstatesWithGroups } from '../hooks/useEstates';
import { useUserTypes } from '../hooks/useUserTypes';
import { useAllBusinessesCrud } from '../hooks/useBusinessCrud';
import * as samApi from '../api/samApi';
import { useNationalities, formatNationality } from '../hooks/useNationalities';

const DrawerContent = styled('div', {
  padding: '0',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const DrawerHeader = styled('div', {
  padding: '24px 24px 20px',
  borderBottom: '1px solid #E5E7EB',
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
});

const AvatarCircle = styled('div', {
  width: '44px',
  height: '44px',
  borderRadius: '50%',
  backgroundColor: 'var(--tenant-primary, #111827)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const DrawerBody = styled('div', {
  flex: 1,
  overflowY: 'auto',
  padding: '0',
});

const SectionBlock = styled('div', {
  padding: '20px 24px',
  borderBottom: '1px solid #F3F4F6',
});

const SectionTitle = styled('div', {
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase' as any,
  letterSpacing: '0.05em',
  color: '#9CA3AF',
  marginBottom: '14px',
});

const InfoGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
});

const InfoItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const InfoLabel = styled('span', {
  fontSize: '11px',
  fontWeight: 600,
  color: '#9CA3AF',
  textTransform: 'uppercase' as any,
  letterSpacing: '0.03em',
});

const InfoValue = styled('span', {
  fontSize: '14px',
  color: '#111827',
  fontWeight: 500,
  wordBreak: 'break-word',
});

const GroupTag = styled('span', {
  display: 'inline-block',
  padding: '3px 10px',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: 500,
  backgroundColor: '#F3F4F6',
  color: '#374151',
  marginRight: '6px',
  marginBottom: '4px',
});

const ActionBtn = styled('button', {
  width: '100%',
  padding: '10px 16px',
  borderRadius: '8px',
  border: '1px solid #E5E7EB',
  backgroundColor: '#F9FAFB',
  color: '#374151',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.15s ease',
  ':hover': {
    backgroundColor: '#F3F4F6',
  },
  ':disabled': {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
});

const DangerActionBtn = styled('button', {
  width: '100%',
  padding: '10px 16px',
  borderRadius: '8px',
  border: '1px solid #E5E7EB',
  backgroundColor: '#fff',
  color: '#DC2626',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.15s ease',
  ':hover': {
    backgroundColor: '#FEF2F2',
  },
  ':disabled': {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
});

const PrimaryActionBtn = styled('button', {
  width: '100%',
  padding: '10px 16px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: 'var(--tenant-primary, #111827)',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.15s ease',
  ':hover': {
    backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)',
  },
  ':disabled': {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
});

const AVAILABLE_GROUPS: Array<{ id: string; label: string; description: string }> = [
  { id: 'business_admin', label: 'Business admin', description: 'Everything in their own business' },
  { id: 'business_invitation', label: 'Business Invitations', description: 'Manage business invitations' },
  { id: 'business_residential_invitation', label: 'Residential Invitations', description: 'Manage residential invitations' },
  { id: 'business_invitation_all', label: 'All Invitations', description: 'Manage all invitations across businesses' },
  { id: 'business_payments', label: 'Payments', description: 'Payments & products' },
  { id: 'business_services', label: 'Services', description: 'Manage services' },
  { id: 'business_bookings', label: 'Bookings', description: 'Manage bookings' },
  { id: 'business_events', label: 'Events', description: 'Manage events' },
  { id: 'business_event_dashboard', label: 'Event Dashboard (read-only)', description: 'View event dashboards & export CSV only' },
  { id: 'business_users', label: 'Users', description: 'User management' },
  { id: 'business_estates', label: 'Estates', description: 'Estate management' },
  { id: 'business_property_manager', label: 'Property Manager', description: 'Property admin' },
  { id: 'business_cms', label: 'CMS', description: 'Content management' },
  { id: 'business_roles', label: 'Roles', description: 'Role management' },
  { id: 'business_insights', label: 'Insights', description: 'Analytics access' },
  { id: 'tenantSettings', label: 'Tenant Settings', description: 'Tenant configuration' },
  { id: 'dashboard', label: 'Dashboard', description: 'Dashboard only (read-only)' },
];

interface UserDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: SAMUser | null;
  onUserUpdated?: () => void;
}

export const UserDetailDrawer: React.FC<UserDetailDrawerProps> = ({ isOpen, onClose, user, onUserUpdated }) => {
  const { resetPassword, isResetting } = useResetPassword(user?.sub);
  const { changePassword, isChanging } = useChangePassword(user?.sub);
  const { disableUser, isDisabling } = useDisableUser(user?.sub);
  const { enableUser, isEnabling } = useEnableUser(user?.sub);
  const { deleteUser, isDeleting } = useDeleteUser(user?.sub);
  const { isSetPassword } = useUserPermissions();

  const [newPassword, setNewPassword] = useState('');
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [loadingPicture, setLoadingPicture] = useState(false);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [savingRoles, setSavingRoles] = useState(false);
  const [editingRoles, setEditingRoles] = useState(false);

  // Business assignment (custom:business_id) — editable via dropdown.
  const { data: businessesData } = useAllBusinessesCrud();
  const businessOptions = React.useMemo(
    () => (Array.isArray(businessesData) ? businessesData : []).map((b: any) => ({
      id: String(b.id),
      label: b.name || `Business ${b.id}`,
    })),
    [businessesData],
  );
  const currentBusinessId = hasBusinessId(user?.custom_attributes?.business_id)
    ? String(user?.custom_attributes?.business_id)
    : '';
  const businessNameFor = (id: string) =>
    businessOptions.find((b) => b.id === id)?.label || (id ? `Business ${id}` : '—');
  const [editingBusiness, setEditingBusiness] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<Array<{ id: string; label: string }>>([]);
  const [savingBusiness, setSavingBusiness] = useState(false);
  // SAM-1297: an account with no business role that is given a business gets its role in the same save, or it enters
  // the app with nothing to see («No roles assigned»), as happened to Garrido on 28 Oct. Operator is marked by default.
  const [newBusinessRoles, setNewBusinessRoles] = useState<string[]>([DEFAULT_BUSINESS_ROLE]);
  const accountGroups = userGroups.length > 0 ? userGroups : user?.groups || [];
  const needsRoleWithBusiness = !accountGroups.some(groupNeedsBusiness);
  const toggleNewBusinessRole = (id: string) =>
    setNewBusinessRoles((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  // Estate assignment for property managers
  const { data: estatesData } = useEstatesWithGroups();
  const { userTypes } = useUserTypes();
  const [dbUserId, setDbUserId] = useState<number | null>(null);
  const [assignedEstates, setAssignedEstates] = useState<any[]>([]);
  const [selectedNewEstate, setSelectedNewEstate] = useState<Array<{ id: string; label: string }>>([]);
  const [selectedUserType, setSelectedUserType] = useState<Array<{ id: number; label: string }>>([]);
  const [linkingEstate, setLinkingEstate] = useState(false);

  const isPropertyManagerUser = (userGroups.length > 0 ? userGroups : user?.groups || []).includes('business_property_manager');

  const estateOptions = React.useMemo(() => {
    const estates = (estatesData as any)?.data || estatesData || [];
    if (!Array.isArray(estates)) return [];
    return estates.map((e: any) => ({
      id: String(e.id),
      label: `${e.name}${e.estate_group?.name ? ` — ${e.estate_group.name}` : ''}`,
    }));
  }, [estatesData]);

  // Load DB user ID and assigned estates when a property manager is opened
  useEffect(() => {
    if (!isOpen || !isPropertyManagerUser || !user || (!user.sub && user.user_id == null)) {
      setDbUserId(null);
      setAssignedEstates([]);
      return;
    }
    // SAM-801: by the numeric user_id of the listing (#646); a null id is a user with no row in this tenant.
    const dbIdPromise = typeof user.user_id === 'number'
      ? Promise.resolve(user.user_id)
      : fetchDbUser(user).then((row) => row?.id ?? null);
    dbIdPromise
      .then((id) => {
        if (!id) return;
        setDbUserId(id);
        return defaultInstance({ method: 'GET', url: '/user_estate', params: { user_id: id, expand: 'estate,estate_id', limit: 100 } });
      })
      .then((resp: any) => {
        if (!resp) return;
        const items: any[] = resp?.data?.data ?? resp?.data ?? [];
        setAssignedEstates(Array.isArray(items) ? items : []);
      })
      .catch(() => {});
  }, [isOpen, isPropertyManagerUser, user?.sub, user?.user_id]);

  const handleLinkEstate = async () => {
    if (!dbUserId || selectedNewEstate.length === 0 || selectedUserType.length === 0) return;
    setLinkingEstate(true);
    try {
      await directApiInstance({
        method: 'POST',
        url: '/user_estate',
        data: { user_id: dbUserId, estate_id: Number(selectedNewEstate[0].id), user_type_id: selectedUserType[0].id, status_id: 1 },
        // S-60: the estate goes to the account that was opened, or the server refuses (409 target_mismatch).
        params: user?.sub ? { expected_sub: user.sub } : undefined,
      });
      toaster.positive('Estate linked successfully', { autoHideDuration: 3000 });
      setSelectedNewEstate([]);
      setSelectedUserType([]);
      // Refresh assigned estates list
      const resp: any = await defaultInstance({ method: 'GET', url: '/user_estate', params: { user_id: dbUserId, expand: 'estate,estate_id', limit: 100 } });
      const items: any[] = resp?.data?.data ?? resp?.data ?? [];
      setAssignedEstates(Array.isArray(items) ? items : []);
    } catch (err) {
      toaster.negative(isTargetMismatch(err) ? TARGET_MISMATCH_MESSAGE : 'Failed to link estate', { autoHideDuration: 5000 });
    } finally {
      setLinkingEstate(false);
    }
  };

  // Profile picture from the DB user row, read by the listing's numeric user_id (SAM-801, #629/#646).
  useEffect(() => {
    if (!user || (!user.sub && user.user_id == null)) {
      setProfilePicture(null);
      return;
    }
    setLoadingPicture(true);
    fetchDbUser<{ id?: number; profile_picture?: string | null; photo_url?: string | null }>(user)
      .then((dbUser) => {
        if (dbUser?.profile_picture) {
          setProfilePicture(dbUser.profile_picture);
        } else if (dbUser?.photo_url) {
          setProfilePicture(dbUser.photo_url);
        } else {
          setProfilePicture(null);
        }
      })
      .catch(() => setProfilePicture(null))
      .finally(() => setLoadingPicture(false));
  }, [user?.sub, user?.user_id]);

  // Load user's current Cognito groups
  useEffect(() => {
    if (!user?.username) {
      setUserGroups([]);
      return;
    }
    setEditingRoles(false);
    // Use groups from the user object initially
    setUserGroups(user.groups || []);
    // Refresh from Lambda API
    samApi.getUserGroups(user.username)
      .then((res: any) => {
        const data = res?.data || res;
        const groups = data?.groups || [];
        setUserGroups(groups.map((g: any) => g.name || g.GroupName || '').filter(Boolean));
      })
      .catch(() => {});
  }, [user?.username, isOpen]);

  const handleToggleGroup = (groupId: string) => {
    setUserGroups((prev) =>
      prev.includes(groupId) ? prev.filter((g) => g !== groupId) : [...prev, groupId]
    );
  };

  const handleSaveRoles = async () => {
    if (!user?.username) return;
    setSavingRoles(true);
    try {
      const currentGroups = user.groups || [];
      const toAdd = userGroups.filter((g) => !currentGroups.includes(g));
      const toRemove = currentGroups.filter((g) => !userGroups.includes(g));

      // A business role on an account with no business leaves it locked out; the server refuses it too (409).
      if (!currentBusinessId && toAdd.some(groupNeedsBusiness)) {
        toaster.negative(businessRequiredMessage(), { autoHideDuration: 4000 });
        return;
      }

      for (const group of toAdd) {
        await samApi.addUserToGroup(user.username, group, user.sub);
      }
      for (const group of toRemove) {
        await samApi.removeUserFromGroup(user.username, group, user.sub);
      }

      toaster.positive(`Roles updated for ${user.username}`, { autoHideDuration: 3000 });
      setEditingRoles(false);
      onUserUpdated?.();
    } catch (err: any) {
      toaster.negative(
        isTargetMismatch(err) ? TARGET_MISMATCH_MESSAGE
          : isBusinessIdRequired(err) ? businessRequiredMessage()
          : err?.message || 'Failed to update roles',
        { autoHideDuration: 3000 },
      );
    } finally {
      setSavingRoles(false);
    }
  };

  const handleSaveBusiness = async () => {
    if (!user?.username) return;
    const newId = selectedBusiness[0]?.id;
    if (!newId) return;
    // The server asks for the business before any role (409 business_id_required), so: attribute first, then groups.
    const rolesToGive = needsRoleWithBusiness ? newBusinessRoles : [];
    if (needsRoleWithBusiness && rolesToGive.length === 0) return;
    setSavingBusiness(true);
    let businessSaved = false;
    try {
      await samApi.updateUserAttributes(user.username, { business_id: newId }, user.sub);
      businessSaved = true;
      for (const group of rolesToGive) {
        await samApi.addUserToGroup(user.username, group, user.sub);
      }
      toaster.positive(rolesToGive.length > 0 ? `Business and role saved for ${user.username}` : `Business updated for ${user.username}`, { autoHideDuration: 3000 });
      setEditingBusiness(false);
      onUserUpdated?.();
    } catch (err: any) {
      const reason = isTargetMismatch(err) ? TARGET_MISMATCH_MESSAGE : err?.response?.data?.error || err?.message || 'Failed to update business';
      // Business saved but the role was not: say so and refresh, the account must not look untouched.
      toaster.negative(businessSaved ? `Business saved, but the role was not: ${reason}. Give it in Roles & Permissions.` : reason, { autoHideDuration: 5000 });
      if (businessSaved) { setEditingBusiness(false); onUserUpdated?.(); }
    } finally {
      setSavingBusiness(false);
    }
  };

  if (!user) return null;

  const handleResetPasswordEmail = () => {
    resetPassword(user.username, { temporary: true });
  };

  const handleSetPassword = () => {
    if (!newPassword || newPassword.length < 8) return;
    changePassword(user.username, newPassword);
    setNewPassword('');
    setShowPasswordInput(false);
  };

  const handleDisableUser = () => {
    disableUser(user.username, { onSuccess: () => onUserUpdated?.() });
  };

  const handleEnableUser = () => {
    enableUser(user.username, { onSuccess: () => onUserUpdated?.() });
  };

  const handleDeleteUser = () => {
    deleteUser(user.username, {
      onSuccess: () => {
        setShowDeleteConfirm(false);
        onClose();
        onUserUpdated?.();
      }
    });
  };

  const isUserDisabled = !user.enabled;
  const businessGroups = user.groups.filter((g) => g.startsWith('business'));
  const displayName = user.name && user.family_name
    ? `${user.name} ${user.family_name}`
    : user.name || user.family_name || user.username;

  const statusDate = new Date(user.created);
  const formattedDate = statusDate.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  return (
    <Drawer
      isOpen={isOpen}
      autoFocus
      onClose={onClose}
      size={SIZE.default}
      anchor={ANCHOR.right}
      overrides={{
        Root: { style: { zIndex: 9999 } },
        DrawerContainer: { style: { width: '520px', zIndex: 10000 } },
        DrawerBody: { style: { marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 } },
      }}
    >
      <DrawerContent>
        {/* Header */}
        <DrawerHeader>
          <AvatarCircle>
            {profilePicture ? (
              <S3Image
                imageKey={profilePicture}
                alt={displayName}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <User size={20} color="#fff" />
            )}
          </AvatarCircle>
          <div>
            <HeadingSmall margin={0} style={{ fontSize: '18px', fontWeight: 600 }}>{displayName}</HeadingSmall>
            <ParagraphSmall margin="2px 0 0" color="#6B7280" style={{ fontSize: '13px' }}>{user.email}</ParagraphSmall>
          </div>
        </DrawerHeader>

        <DrawerBody>
          {/* User Info */}
          <SectionBlock>
            <SectionTitle>Information</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Username</InfoLabel>
                <InfoValue>{user.username}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Created</InfoLabel>
                <InfoValue>{formattedDate}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  <span style={{
                    display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
                    backgroundColor: !isUserDisabled ? '#10B981' : '#EF4444', marginRight: '8px',
                  }} />
                  {isUserDisabled ? 'Disabled' : user.status}
                </InfoValue>
              </InfoItem>
              {user.phone_number && (
                <InfoItem>
                  <InfoLabel>Phone</InfoLabel>
                  <InfoValue>{user.phone_number}</InfoValue>
                </InfoItem>
              )}
            </InfoGrid>
          </SectionBlock>

          {/* Groups / Roles */}
          <SectionBlock>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <SectionTitle style={{ marginBottom: 0 }}>Roles & Permissions</SectionTitle>
              {!editingRoles ? (
                <button
                  onClick={() => setEditingRoles(true)}
                  style={{ background: 'none', border: 'none', color: 'var(--tenant-primary, #111827)', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
                >
                  Edit
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => { setEditingRoles(false); setUserGroups(user.groups || []); }}
                    style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '13px' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveRoles}
                    disabled={savingRoles}
                    style={{ background: 'var(--tenant-primary, #111827)', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Save size={12} />
                    {savingRoles ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
            {editingRoles ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {!currentBusinessId && (
                  <ParagraphSmall margin={0} color="#B45309" style={{ fontSize: '12px' }}>
                    {businessRequiredMessage()} {businessRequiredHint()}
                  </ParagraphSmall>
                )}
                {AVAILABLE_GROUPS.map((group) => (
                  <div key={group.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #F9FAFB' }}>
                    <Checkbox
                      checked={userGroups.includes(group.id)}
                      disabled={!currentBusinessId && groupNeedsBusiness(group.id) && !userGroups.includes(group.id)}
                      onChange={() => handleToggleGroup(group.id)}
                      overrides={{
                        Root: { style: { marginBottom: 0 } },
                        Checkmark: {
                          style: ({ $checked }: { $checked: boolean }) => ({
                            width: '18px',
                            height: '18px',
                            backgroundColor: $checked ? 'var(--tenant-primary, #111827)' : 'transparent',
                            borderColor: $checked ? 'var(--tenant-primary, #111827)' : '#D1D5DB',
                          }),
                        },
                      }}
                      labelPlacement={LABEL_PLACEMENT.right}
                    >
                      <span style={{ fontSize: '13px', fontWeight: 500, color: '#111827' }}>{group.label}</span>
                    </Checkbox>
                    <span style={{ fontSize: '11px', color: '#9CA3AF', marginLeft: 'auto' }}>{group.description}</span>
                  </div>
                ))}
              </div>
            ) : (
              userGroups.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {userGroups.map((group) => {
                    const meta = AVAILABLE_GROUPS.find((g) => g.id === group);
                    return <GroupTag key={group}>{meta?.label || group.replace('business_', '')}</GroupTag>;
                  })}
                </div>
              ) : (
                <ParagraphSmall margin={0} color="#9CA3AF" style={{ fontStyle: 'italic', fontSize: '13px' }}>
                  No roles assigned
                </ParagraphSmall>
              )
            )}
          </SectionBlock>

          {/* Business assignment (custom:business_id) — shown as name + editable dropdown */}
          <SectionBlock>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <SectionTitle style={{ margin: 0 }}>Business</SectionTitle>
              {!editingBusiness ? (
                <button
                  onClick={() => {
                    setSelectedBusiness(currentBusinessId ? [{ id: currentBusinessId, label: businessNameFor(currentBusinessId) }] : []);
                    setNewBusinessRoles([DEFAULT_BUSINESS_ROLE]);
                    setEditingBusiness(true);
                  }}
                  style={{ background: 'none', border: 'none', color: 'var(--tenant-primary, #2563EB)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Edit
                </button>
              ) : (
                <button
                  onClick={() => setEditingBusiness(false)}
                  style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              )}
            </div>
            {!editingBusiness ? (
              <InfoValue style={{ fontSize: '14px', fontWeight: 500 }}>
                {currentBusinessId ? businessNameFor(currentBusinessId) : <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not assigned</span>}
              </InfoValue>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Select
                  options={businessOptions}
                  value={selectedBusiness}
                  onChange={(p) => setSelectedBusiness(p.value as any)}
                  placeholder="Select business..."
                  searchable
                  clearable={false}
                  overrides={{ Popover: { props: { overrides: { Body: { style: { zIndex: 10002 } } } } } }}
                />
                {needsRoleWithBusiness && (
                  <div role="group" aria-label="Role" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <ParagraphSmall margin={0} color="#6B7280" style={{ fontSize: '12px' }}>
                      This account has no role yet. Choose at least one so it sees something when it signs in.
                    </ParagraphSmall>
                    {BUSINESS_USER_ROLES.map((r) => (
                      <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Checkbox
                          checked={newBusinessRoles.includes(r.id)}
                          onChange={() => toggleNewBusinessRole(r.id)}
                          overrides={{ Root: { style: { marginBottom: 0 } } }}
                          labelPlacement={LABEL_PLACEMENT.right}
                        >
                          <span style={{ fontSize: '13px', fontWeight: 500, color: '#111827' }}>{r.name}</span>
                        </Checkbox>
                        <span style={{ fontSize: '11px', color: '#9CA3AF', marginLeft: 'auto' }}>{r.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
                <PrimaryActionBtn
                  onClick={handleSaveBusiness}
                  disabled={
                    savingBusiness || selectedBusiness.length === 0 || selectedBusiness[0]?.id === currentBusinessId
                    || (needsRoleWithBusiness && newBusinessRoles.length === 0)
                  }
                >
                  {savingBusiness ? 'Saving…' : needsRoleWithBusiness ? 'Save' : 'Save business'}
                </PrimaryActionBtn>
              </div>
            )}
          </SectionBlock>

          {/* Custom Attributes */}
          {user.custom_attributes && Object.keys(user.custom_attributes).filter(k => k !== 'tenant_id' && k !== 'tenant_ids' && k !== 'business_id').length > 0 && (
            <SectionBlock>
              <SectionTitle>Attributes</SectionTitle>
              <InfoGrid>
                {Object.entries(user.custom_attributes)
                  .filter(([key]) => key !== 'tenant_id' && key !== 'tenant_ids' && key !== 'business_id')
                  .map(([key, value]) => {
                    const labelMap: Record<string, string> = {
                      firstName: 'First Name', lastName: 'Last Name', idNumber: 'ID Number',
                      idType: 'ID Type', idIssuer: 'ID Issuer', idExpiration: 'ID Expiration',
                      nationality: 'Nationality', birthdate: 'Birth Date', onboardingStep: 'Onboarding Step',
                      status: 'Status', role: 'Role', mainEstate: 'Main Estate', main_estate: 'Main Estate',
                      pictureIdFront: 'ID Front', pictureIdBack: 'ID Back', pictureIdType: 'ID Type',
                      business_id: 'Business ID',
                    };
                    const label = labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    return (
                      <InfoItem key={key}>
                        <InfoLabel>{label}</InfoLabel>
                        <InfoValue style={{ fontSize: '13px' }}>
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </InfoValue>
                      </InfoItem>
                    );
                  })}
              </InfoGrid>
            </SectionBlock>
          )}

          {/* Estate Assignment — only for property managers */}
          {isPropertyManagerUser && (
            <SectionBlock>
              <SectionTitle>Estate Assignment</SectionTitle>

              {/* Current estates */}
              {assignedEstates.length > 0 ? (
                <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {assignedEstates.map((ue: any, i: number) => {
                    const name = ue.estate?.name || (typeof ue.estate_id === 'object' ? ue.estate_id?.name : null) || `Estate ${ue.estate_id}`;
                    const group = ue.estate_group?.name || ue.estate?.estate_group?.name || '';
                    const status = ue.status_id === 2 ? 'Approved' : 'Pending';
                    const color = ue.status_id === 2 ? '#065F46' : '#92400E';
                    const bg = ue.status_id === 2 ? '#D1FAE5' : '#FEF3C7';
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#F9FAFB', fontSize: '13px' }}>
                        <span style={{ fontWeight: 500 }}>{name}{group ? ` — ${group}` : ''}</span>
                        <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '8px', backgroundColor: bg, color }}>{status}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <ParagraphSmall margin="0 0 12px" color="#9CA3AF" style={{ fontStyle: 'italic', fontSize: '13px' }}>
                  {dbUserId ? 'No estates assigned yet' : 'Loading...'}
                </ParagraphSmall>
              )}

              {/* Add new estate */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Select
                  options={estateOptions}
                  value={selectedNewEstate}
                  onChange={(p) => setSelectedNewEstate(p.value as any)}
                  placeholder="Search and select estate..."
                  disabled={linkingEstate || !dbUserId}
                  searchable
                  clearable
                  overrides={{ Popover: { props: { overrides: { Body: { style: { zIndex: 10002 } } } } } }}
                />
                <Select
                  options={(userTypes || []).map((t: any) => ({ id: t.id, label: t.name || `Type ${t.id}` }))}
                  value={selectedUserType}
                  onChange={(p) => setSelectedUserType(p.value as any)}
                  placeholder="Select user type / role..."
                  disabled={linkingEstate || !dbUserId}
                  clearable
                  overrides={{ Popover: { props: { overrides: { Body: { style: { zIndex: 10002 } } } } } }}
                />
                <PrimaryActionBtn
                  onClick={handleLinkEstate}
                  disabled={linkingEstate || !dbUserId || selectedNewEstate.length === 0 || selectedUserType.length === 0}
                >
                  <Link size={14} />
                  {linkingEstate ? 'Linking...' : 'Link Estate'}
                </PrimaryActionBtn>
              </div>
              {!dbUserId && (
                <ParagraphSmall margin="8px 0 0" color="#EF4444" style={{ fontSize: '12px' }}>
                  DB user not found — this user may not have logged in yet.
                </ParagraphSmall>
              )}
            </SectionBlock>
          )}

          {/* Password Management */}
          <SectionBlock>
            <SectionTitle>Password</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <ActionBtn
                onClick={handleResetPasswordEmail}
                disabled={isResetting || isChanging}
              >
                <Mail size={14} />
                {isResetting ? 'Sending...' : 'Send Password Reset Email'}
              </ActionBtn>

              {isSetPassword && !showPasswordInput && (
                <ActionBtn
                  onClick={() => setShowPasswordInput(true)}
                  disabled={isResetting || isChanging}
                >
                  <Key size={14} />
                  Set New Password
                </ActionBtn>
              )}

              {showPasswordInput && (
                <div style={{ marginTop: '4px' }}>
                  <FormControl caption="Minimum 8 characters">
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.currentTarget.value)}
                      placeholder="Enter new password"
                      disabled={isChanging}
                      overrides={{
                        Root: { style: { borderRadius: '8px' } },
                      }}
                    />
                  </FormControl>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <ActionBtn
                      onClick={() => { setShowPasswordInput(false); setNewPassword(''); }}
                      disabled={isChanging}
                      style={{ flex: 1 } as any}
                    >
                      Cancel
                    </ActionBtn>
                    <PrimaryActionBtn
                      onClick={handleSetPassword}
                      disabled={!newPassword || newPassword.length < 8 || isChanging}
                      style={{ flex: 1 } as any}
                    >
                      {isChanging ? 'Updating...' : 'Update Password'}
                    </PrimaryActionBtn>
                  </div>
                </div>
              )}
            </div>
          </SectionBlock>

          {/* Account Actions */}
          <SectionBlock style={{ borderBottom: 'none' }}>
            <SectionTitle>Account</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {!isUserDisabled ? (
                <ActionBtn
                  onClick={handleDisableUser}
                  disabled={isDisabling}
                >
                  <ShieldOff size={14} />
                  {isDisabling ? 'Disabling...' : 'Disable User'}
                </ActionBtn>
              ) : (
                <ActionBtn
                  onClick={handleEnableUser}
                  disabled={isEnabling}
                >
                  <ShieldCheck size={14} />
                  {isEnabling ? 'Enabling...' : 'Enable User'}
                </ActionBtn>
              )}

              {!showDeleteConfirm ? (
                <DangerActionBtn
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={!isUserDisabled || isDeleting}
                >
                  <Trash2 size={14} />
                  Delete User
                </DangerActionBtn>
              ) : (
                <div style={{ padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                  <ParagraphSmall margin="0 0 12px" color="#374151" style={{ fontSize: '13px', fontWeight: 500 }}>
                    This action is permanent and cannot be undone.
                  </ParagraphSmall>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <ActionBtn
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      style={{ flex: 1 } as any}
                    >
                      Cancel
                    </ActionBtn>
                    <DangerActionBtn
                      onClick={handleDeleteUser}
                      disabled={isDeleting}
                      style={{ flex: 1 } as any}
                    >
                        <Trash2 size={14} />
                        {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                      </DangerActionBtn>
                  </div>
                  </div>
              )}

              {!isUserDisabled && (
                <ParagraphSmall margin="4px 0 0" color="#9CA3AF" style={{ fontSize: '11px' }}>
                  User must be disabled before deletion
                </ParagraphSmall>
              )}
            </div>
          </SectionBlock>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
